import React, { useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import clsx from 'clsx';
import { normalizeText } from '../../utils/text';
import { useEscapeToClose } from './useEscapeToClose';

/** Search box + list at its tallest, used to decide whether to flip upwards. */
const DROPDOWN_MAX_HEIGHT = 300;

/** Gap between the trigger and the floating panel. */
const DROPDOWN_OFFSET = 8;

/**
 * @typedef {object} SelectOption
 * @property {string|number} value
 * @property {string} label
 * @property {string} [shortLabel] - Shown on the trigger instead of `label`,
 *   for options whose full text only makes sense inside the open list.
 */

/**
 * Select with a built-in search box.
 *
 * Three looks share the same behaviour:
 * - `filter` (default) — the compact inline pill used in toolbars.
 * - `form` — a full-width field matching the modal inputs.
 * - `bare` — no border or background, to be embedded inside another field.
 *
 * The panel is mounted on `document.body` through a portal and positioned from
 * the trigger's box. Rendering it in place would let any ancestor with
 * `overflow` (a scrollable modal body, for instance) clip it — something no
 * `z-index` can undo. It flips above the trigger when there is not enough room
 * below.
 *
 * Closing on an outside click is handled by a full-screen overlay rather than
 * a document listener, per the project's UI rules.
 *
 * @param {object} props
 * @param {string} [props.label]
 * @param {string|number} props.value
 * @param {(value: string|number) => void} props.onChange
 * @param {Array<SelectOption>} [props.options=[]]
 * @param {string} [props.placeholder='Seleccionar...']
 * @param {string} [props.searchPlaceholder='Buscar...']
 * @param {'filter'|'form'|'bare'} [props.variant='filter']
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.hasError=false]
 * @param {number} [props.panelMinWidth] - Floor for the panel width, so a
 *   narrow trigger can still open a readable list.
 * @param {string} [props.testId] - Names this select for end-to-end tests. Two
 *   selects in the same toolbar are otherwise told apart only by their visible
 *   label, which the `bare` variant does not render at all.
 * @param {string} [props.className]
 */
export const FilterableSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Seleccionar...',
  searchPlaceholder = 'Buscar...',
  variant = 'filter',
  disabled = false,
  hasError = false,
  panelMinWidth,
  testId,
  className = ''
}) => {
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState(null);

  const isForm = variant === 'form';
  const isPill = variant === 'filter';
  const isBare = variant === 'bare';
  const selectedOption = options.find(o => o.value === value);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const term = normalizeText(search);
    return options.filter(o => normalizeText(o.label).includes(term));
  }, [options, search]);

  const close = () => {
    setIsOpen(false);
    setSearch('');
  };

  // In the shared stack: Escape in an open list closes the list, not the
  // dialog the select lives in.
  useEscapeToClose(isOpen, close);

  const open = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < DROPDOWN_MAX_HEIGHT && rect.top > spaceBelow;

    setPosition({
      left: rect.left,
      width: rect.width,
      top: flipUp ? undefined : rect.bottom + DROPDOWN_OFFSET,
      bottom: flipUp ? window.innerHeight - rect.top + DROPDOWN_OFFSET : undefined,
    });
    setIsOpen(true);
  };

  const handleSelect = (optValue) => {
    onChange(optValue);
    close();
  };

  const dropdown = isOpen && position && (
    <>
      <div className="fixed inset-0 z-[10000]" onClick={close} onWheel={close} />
      <div
        style={{
          position: 'fixed',
          top: position.top,
          bottom: position.bottom,
          left: isPill ? position.left + position.width / 2 : position.left,
          width: position.width,
          minWidth: panelMinWidth,
        }}
        data-testid={testId ? `${testId}-panel` : 'select-panel'}
        className={clsx(
          'z-[10001] bg-white dark:bg-[#1a1a1a] border border-border shadow-2xl rounded-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200',
          isPill && '-translate-x-1/2 min-w-[200px]'
        )}
      >
        <div className="p-2 border-b border-border flex items-center gap-2">
          <Search size={14} className="text-text-muted flex-shrink-0" />
          <input
            type="text"
            autoFocus
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid={testId ? `${testId}-search` : 'select-search'}
            className="w-full bg-transparent text-sm outline-none text-ninja-dark dark:text-white placeholder:text-text-muted min-w-[150px]"
          />
        </div>
        <div role="listbox" className="max-h-60 overflow-y-auto p-1 custom-scrollbar">
          {filteredOptions.length === 0 ? (
            <div className="p-3 text-sm text-text-muted text-center">Sin resultados</div>
          ) : (
            filteredOptions.map(opt => (
              <div
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
                data-testid={testId ? `${testId}-option` : 'select-option'}
                data-value={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={clsx(
                  "px-3 py-2 text-sm rounded-lg cursor-pointer transition-colors flex items-center justify-between gap-3",
                  opt.value === value
                    ? "bg-ninja-green/10 text-ninja-green font-medium"
                    : "text-ninja-dark dark:text-white hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]"
                )}
              >
                <span className="truncate">{opt.label}</span>
                {opt.value === value && (
                  <span className="w-1.5 h-1.5 rounded-full bg-ninja-green flex-shrink-0"></span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="relative">
      {isForm && label && (
        <label className="block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5">
          {label}
        </label>
      )}

      <div
        ref={triggerRef}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label || placeholder}
        data-testid={testId ? `${testId}-trigger` : 'select-trigger'}
        onClick={() => {
          if (disabled) return;
          if (isOpen) {
            close();
            return;
          }
          open();
        }}
        className={clsx(
          'flex items-center gap-2 group transition-colors',
          isForm && 'w-full border rounded-xl bg-ninja-gray/50 dark:bg-[#111] px-4 py-2.5',
          isPill &&
            'border rounded-xl bg-white dark:bg-[#1a1a1a] px-3 py-2 hover:bg-ninja-gray/20 dark:hover:bg-[#222]',
          // `bare` brings no chrome of its own: the field it sits in owns the
          // border, the background and the focus ring.
          isBare && 'h-full px-4 py-2.5',
          !isBare && (hasError ? 'border-danger' : 'border-border'),
          disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
          className
        )}
      >
        {label && isPill && (
          <span className="text-sm text-text-secondary whitespace-nowrap">{label}</span>
        )}
        <div className="flex items-center gap-1 flex-1 justify-between min-w-0">
          <span
            className={clsx(
              'text-sm truncate',
              selectedOption || isPill
                ? 'font-medium text-ninja-dark dark:text-white'
                : 'text-text-muted'
            )}
          >
            {selectedOption
              ? selectedOption.shortLabel || selectedOption.label
              : placeholder}
          </span>
          <ChevronDown
            size={14}
            className={clsx(
              'text-text-muted transition-all flex-shrink-0',
              !disabled && 'group-hover:text-ninja-dark dark:group-hover:text-white',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </div>

      {isOpen && position && createPortal(dropdown, document.body)}
    </div>
  );
};
